import { useState } from "react"
import {
    Box,
    Chip,
    Container,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import { Search } from "@mui/icons-material"

import EmptyState from "../../../components/EmptyState"
import PageCtaBand from "../../../components/PageCtaBand"
import QuestionAnswer from "../../../components/QuestionAnswer"
import StyledPagination from "../../../components/StyledPagination"
import { useEnquire } from "../../../components/EnquireModal"
import { PHONE_DISPLAY, PHONE_HREF } from "../../../constants/contactInfo"
import { usePaginatedFAQs } from "../../../hooks/useFAQs"
import { overlineSx, LIGHT_SECTION_BACKGROUND } from "../../../styles/sectionStyles"

const PAGE_SIZE = 8

const QuestionAnswerList = () => {
    const openEnquire = useEnquire()
    const [searchInput, setSearchInput] = useState("")
    const { faqs, totalFaqs, currentPage, setCurrentPage, totalPages, loading, searchQuery } =
        usePaginatedFAQs(PAGE_SIZE, searchInput)

    const rangeStart = totalFaqs === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
    const rangeEnd = Math.min(currentPage * PAGE_SIZE, totalFaqs)
    const isSearching = searchQuery.length > 0

    return (
        <>
            <Box
                component="section"
                id="faq-list"
                sx={{
                    position: "relative",
                    py: { xs: 6, md: 9 },
                    background: LIGHT_SECTION_BACKGROUND,
                }}
            >
                <Container maxWidth="lg">
                    <Stack
                        spacing={1.5}
                        textAlign="center"
                        mb={{ xs: 4, md: 5 }}
                        maxWidth={640}
                        mx="auto"
                        data-aos="fade-up"
                        data-aos-duration="800"
                    >
                        <Typography variant="overline" sx={overlineSx}>
                            Before you enquire
                        </Typography>
                        <Typography
                            variant="h4"
                            fontWeight={800}
                            sx={{ color: "#000435", lineHeight: 1.15 }}
                        >
                            Everything you need to know
                        </Typography>
                        <Typography variant="body1" color="text.secondary" lineHeight={1.7}>
                            Search or browse common questions about hire agreements, insurance,
                            payments, and vehicle use. Tap a question to expand the answer.
                        </Typography>
                    </Stack>

                    <Box mb={3} data-aos="fade-up" data-aos-duration="800">
                        <TextField
                            fullWidth
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search questions — e.g. insurance, deposit, fuel, Tyne Tunnel"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: "text.secondary" }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                maxWidth: 720,
                                mx: "auto",
                                display: "block",
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: "rgba(255,255,255,0.9)",
                                    borderRadius: 2,
                                    "& fieldset": {
                                        borderColor: "rgba(0, 4, 53, 0.1)",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "rgba(0, 163, 255, 0.45)",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#00A3FF",
                                    },
                                },
                            }}
                        />
                    </Box>

                    {(totalFaqs > 0 || isSearching) && (
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            alignItems={{ xs: "stretch", sm: "center" }}
                            justifyContent="space-between"
                            spacing={2}
                            mb={3}
                            data-aos="fade-up"
                            data-aos-duration="800"
                        >
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip
                                    label={`${totalFaqs} question${totalFaqs === 1 ? "" : "s"}${
                                        isSearching ? " found" : ""
                                    }`}
                                    sx={{
                                        fontWeight: 700,
                                        bgcolor: "rgba(0, 163, 255, 0.12)",
                                        color: "#000435",
                                    }}
                                />
                                {isSearching && (
                                    <Chip
                                        label={`“${searchQuery}”`}
                                        onDelete={() => setSearchInput("")}
                                        sx={{
                                            fontWeight: 600,
                                            bgcolor: "rgba(0, 4, 53, 0.06)",
                                            color: "#000435",
                                        }}
                                    />
                                )}
                            </Stack>

                            {totalFaqs > 0 && (
                                <Typography variant="body2" color="text.secondary" textAlign="right">
                                    Showing {rangeStart}–{rangeEnd} of {totalFaqs}
                                </Typography>
                            )}
                        </Stack>
                    )}

                    <Grid
                        container
                        spacing={2}
                        sx={{
                            opacity: loading ? 0.55 : 1,
                            transition: "opacity 0.2s ease",
                            minHeight: loading ? 200 : undefined,
                        }}
                    >
                        {faqs.map((faq) => (
                            <QuestionAnswer {...faq} key={faq._id.toString()} />
                        ))}
                    </Grid>

                    {!loading && faqs.length === 0 && (
                        <EmptyState
                            title={isSearching ? "No matching questions" : "No questions listed yet"}
                            description={
                                isSearching
                                    ? "Try a different search term, or contact us and we will be happy to help."
                                    : "Call or send an enquiry and our team will be happy to help."
                            }
                            actions={
                                isSearching
                                    ? [
                                          {
                                              label: "Clear search",
                                              onClick: () => setSearchInput(""),
                                              variant: "outlined",
                                          },
                                          { label: "Contact us", href: "/contact", variant: "outlined" },
                                      ]
                                    : [
                                          { label: "Enquire now", onClick: () => openEnquire?.() },
                                          { label: "Contact us", href: "/contact", variant: "outlined" },
                                      ]
                            }
                        />
                    )}

                    {totalPages > 1 && (
                        <StyledPagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(_e, page) => setCurrentPage(page)}
                        />
                    )}
                </Container>
            </Box>

            <PageCtaBand
                overline="Still unsure?"
                title="We're here to help"
                description="Every driver's situation is different. Speak to our team about licensing, availability, or which car suits your work."
                gradientPosition="20% 50%"
                actions={[
                    { label: "Contact us", href: "/contact" },
                    {
                        label: `Call ${PHONE_DISPLAY}`,
                        href: PHONE_HREF,
                        variant: "outlined",
                    },
                ]}
            />
        </>
    )
}

export default QuestionAnswerList
